/**
 * Turns two ClarionDoor (CD) quotes into the list of values that differ between them.
 *
 * A line diff of the XML attributes changes by position: CD drivers/vehicles are ~400 lines each with
 * no readable identity, so swapping two cars reads as dozens of phantom edits. Instead, each XML doc is
 * flattened into buckets (Policy, Auto line, one per driver/vehicle, ...) of `field -> value`, and the
 * buckets are compared by identity:
 * - Drivers/vehicles match on CD's injected `ItemIdInfo/SystemId`, falling back to position, and are
 *   labelled with their year/make/model or age/gender from the request.
 * - Repeated elements are keyed by their code (`QuestionAnswer` by `QuestionCd ?? Description`,
 *   `Coverage` by `CoverageCd`), not their tag.
 * - Repeated keys in a bucket are deduped. CD's stored request carries every PersPolicy question twice;
 *   if a copy ever disagrees, both values are kept (joined with ' | ') instead of dropping one.
 *
 * Only the request is diffed. CD's response (premium lines, savings, totals) is raw rating output that
 * staff can't interpret, so it's left out on purpose, as are the `*_Avg` Coverage factors CD injects.
 */

const POLICY = 'Policy';
const AUTO_LINE = 'Auto line';
const OTHER = 'Other';
const SECTION_ORDER = [POLICY, AUTO_LINE, 'Driver', 'Vehicle', OTHER];

const LIST_ITEMS = {
  PersDriver: { section: 'Driver', noun: 'driver' },
  PersVeh: { section: 'Vehicle', noun: 'vehicle' }
};

// Repeated elements identified by a child code; `valueTag` holds the value, other children become
// `<code> > <child>` fields.
const KEYED_ELEMENTS = {
  QuestionAnswer: { keyTags: ['QuestionCd', 'Description'], valueTag: 'Explanation' },
  Coverage: { keyTags: ['CoverageCd'], valueTag: 'Rate' }
};

const directChild = (element, tagName) => Array.from(element.children).find((child) => child.tagName === tagName);

const addValue = ({ fields, field, value, computed }) => {
  // Older rater requests serialized missing residence type and garage city as literal strings.
  const normalizedValue =
    (field === 'residence_ins_type' || field === 'city') && /^(null|undefined)$/i.test(value) ? '' : value;
  const entry = fields.get(field) ?? { values: [], computed: true };
  const { values } = entry;
  // A field echoed as an ordinary input must remain visible.
  entry.computed = entry.computed && computed;
  if (!values.includes(normalizedValue)) {
    values.push(normalizedValue);
  }
  fields.set(field, entry);
};

const collectFields = ({ element, prefix, fields, computed = false }) => {
  for (const child of element.children) {
    const childComputed = computed || child.getAttribute('injected') === '1';
    const keyed = KEYED_ELEMENTS[child.tagName];
    const keyTag = keyed?.keyTags.find((tag) => directChild(child, tag));
    if (!keyTag) {
      addElement({
        element: child,
        field: prefix ? `${prefix}/${child.tagName}` : child.tagName,
        fields,
        computed: childComputed
      });
      continue;
    }
    const code = directChild(child, keyTag).textContent.trim();
    // CD's per-coverage averaging factors (BI_Avg, COLL_Avg, ...) change on nearly every re-rate and
    // mean nothing to staff, so they're left out entirely.
    if (child.tagName === 'Coverage' && code.endsWith('_Avg')) {
      continue;
    }
    // CD also appends risk-group Coverage outputs without the injected attribute.
    // QuestionAnswer inputs such as risk_group_at_inception are intentionally excluded.
    const keyedComputed = childComputed || (child.tagName === 'Coverage' && code.startsWith('risk_group_'));
    if (!directChild(child, keyed.valueTag)) {
      addValue({ fields, field: code, value: '', computed: keyedComputed });
    }
    for (const part of child.children) {
      if (part.tagName !== keyTag) {
        addElement({
          element: part,
          field: part.tagName === keyed.valueTag ? code : `${code} > ${part.tagName}`,
          fields,
          computed: keyedComputed
        });
      }
    }
  }
};

const addElement = ({ element, field, fields, computed = false }) => {
  const isComputed = computed || element.getAttribute('injected') === '1';
  if (element.children.length) {
    collectFields({ element, prefix: field, fields, computed: isComputed });
  } else {
    addValue({ fields, field, value: element.textContent.trim(), computed: isComputed });
  }
};

const getBucket = (buckets, key, props) => {
  if (!buckets.has(key)) {
    buckets.set(key, { ...props, fields: new Map() });
  }
  return buckets.get(key);
};

const parseXml = (xml) => {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new Error('Could not parse ClarionDoor XML');
  }
  // InsuranceSvcRq > PersPkgPolicyQuoteInqRq
  return doc.documentElement.firstElementChild ?? doc.documentElement;
};

const collectBuckets = (xml) => {
  const buckets = new Map();
  const positions = {};
  for (const child of parseXml(xml).children) {
    const tag = child.tagName;
    const listItem = LIST_ITEMS[tag];
    if (listItem) {
      positions[tag] = (positions[tag] ?? 0) + 1;
      const systemId = directChild(child, 'ItemIdInfo')?.getElementsByTagName('SystemId')[0]?.textContent.trim();
      const bucket = getBucket(buckets, `${tag}:${systemId || `#${positions[tag]}`}`, {
        ...listItem,
        systemId,
        position: positions[tag]
      });
      collectFields({ element: child, prefix: '', fields: bucket.fields });
    } else if (tag === 'PersPolicy') {
      collectFields({ element: child, prefix: '', fields: getBucket(buckets, POLICY, { section: POLICY }).fields });
    } else if (tag === 'PersPkgAutoLineBusiness') {
      collectFields({
        element: child,
        prefix: '',
        fields: getBucket(buckets, AUTO_LINE, { section: AUTO_LINE }).fields
      });
    } else {
      addElement({ element: child, field: tag, fields: getBucket(buckets, OTHER, { section: OTHER }).fields });
    }
  }
  return buckets;
};

// CD's XML order can't be mapped back to the policy's cars/drivers arrays (the rater drops soft-rejected
// cars and excluded drivers, and appends trailers), so items are described by what the request itself
// says about them. Only a unique year/make/model-symbol match can supply full vehicle names.
const itemDescription = (bucket, cars) => {
  const value = (field) => bucket.fields.get(field)?.values.find(Boolean);
  if (bucket.noun === 'vehicle') {
    const symbols = [value('model_year'), value('vehicle_make'), value('vehicle_model')];
    const matches = symbols.every(Boolean)
      ? cars.filter((car) =>
          [car.year, car.symbolMake, car.symbolModel].every((part, i) => String(part ?? '').trim() === symbols[i])
        )
      : [];
    const match = matches.length === 1 ? matches[0] : undefined;
    return (match?.make?.trim() && match?.model?.trim() ? [symbols[0], match.make.trim(), match.model.trim()] : symbols)
      .filter(Boolean)
      .join(' ');
  }
  const age = value('age');
  return [age && `age ${age}`, value('DriverInfo/PersonInfo/GenderCd')].filter(Boolean).join(', ');
};

const itemLabel = (bucket, cars) => {
  if (!bucket.noun) {
    return null;
  }
  const description = itemDescription(bucket, cars);
  return `${bucket.section} ${bucket.position}${description ? `: ${description}` : ''}${bucket.systemId ? ` (SystemId ${bucket.systemId})` : ''}`;
};

const formatValues = (values) => (values ? [...values].sort().join(' | ') : null);

const changeType = (hasBefore, hasAfter) => {
  if (!hasBefore) {
    return 'added';
  }
  return hasAfter ? 'changed' : 'removed';
};

export const buildCdDiffRows = ({ beforeRequestXml, afterRequestXml, beforeCars = [], afterCars = [] }) => {
  const beforeBuckets = collectBuckets(beforeRequestXml);
  const afterBuckets = collectBuckets(afterRequestXml);

  const bucketKeys = [...new Set([...beforeBuckets.keys(), ...afterBuckets.keys()])];
  const sortedBuckets = bucketKeys
    .map((key) => ({ before: beforeBuckets.get(key), after: afterBuckets.get(key) }))
    .map((pair) => ({
      ...pair,
      label: itemLabel(pair.after ?? pair.before, pair.after ? afterCars : beforeCars),
      meta: pair.after ?? pair.before
    }))
    .sort(
      (a, b) =>
        SECTION_ORDER.indexOf(a.meta.section) - SECTION_ORDER.indexOf(b.meta.section) ||
        (a.meta.position ?? 0) - (b.meta.position ?? 0)
    );

  return sortedBuckets.flatMap(({ before, after, label, meta }) => {
    // A whole driver/vehicle added or removed is one row, not one per field.
    if (meta.noun && (!before || !after)) {
      return [
        {
          section: meta.section,
          itemLabel: label,
          field: `(entire ${meta.noun})`,
          before: before ? 'Present' : null,
          after: after ? 'Present' : null,
          change: changeType(before, after)
        }
      ];
    }
    const fields = [...new Set([...(before?.fields.keys() ?? []), ...(after?.fields.keys() ?? [])])];
    return fields.flatMap((field) => {
      const beforeValue = formatValues(before?.fields.get(field)?.values);
      const afterValue = formatValues(after?.fields.get(field)?.values);
      if (beforeValue === afterValue) {
        return [];
      }
      return [
        {
          section: meta.section,
          itemLabel: label,
          field,
          before: beforeValue,
          after: afterValue,
          change: changeType(beforeValue !== null, afterValue !== null),
          computed: [before?.fields.get(field), after?.fields.get(field)]
            .filter(Boolean)
            .every((entry) => entry.computed)
        }
      ];
    });
  });
};
